import React from 'react';
import { of } from 'rxjs';
import get from 'lodash.get';
import { defineMessages, FormattedMessage } from 'react-intl';

import { NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';

import * as Actions from '../../../store/printing/print.actions';
import * as ConnectActions from '../../../store/shipstation-connect/ss-connect.actions';
import { DocumentType } from '../../../enums/document-type';

const messages = defineMessages({
  printingLabelSuccessTitle: {
    description: 'printEpics.printingLabelSuccessTitle',
    defaultMessage: 'Printing {docCount, plural,=0 {Label} one {Label} other {Labels}}...',
    id: 'ZHXrpH',
  },
  printingLabelSuccessContent: {
    description: 'printEpics.printingLabelSuccessContent',
    defaultMessage: '{docCount, plural,=0 {Label} one {Label} other {Labels}} sent to {printerName}.',
    id: 'ShRRaF',
  },
  downloadLabelsStartedTitle: {
    description: 'printEpics.downloadLabelsStartedTitle',
    defaultMessage: 'Downloading {docCount, plural,=0 {Label} one {Label} other {Labels}}...',
    id: 'o/Fzww',
  },
  downloadLabelsStartedContent: {
    description: 'printEpics.downloadLabelsStartedContent',
    defaultMessage: '{docCount, plural,=0 {Label} one {Label} other {Labels}} Download Started.',
    id: 'MkGsJa',
  },
  downloadLabelsCompletedTitle: {
    description: 'printEpics.downloadLabelsCompletedTitle',
    defaultMessage: 'Downloading {docCount, plural,=0 {Label} one {Label} other {Labels}}...',
    id: 'Nle58c',
  },
  downloadLabelsCompletedContent: {
    description: 'printEpics.downloadLabelsCompletedContent',
    defaultMessage: '{docCount, plural,=0 {Label} one {Label} other {Labels}} Download Completed.',
    id: 'piUb6X',
  },
  asyncPrintLabelStartedContent: {
    description: 'Notification message shown when generating a label',
    defaultMessage: 'We are generating your {docCount, plural,=0 {Label} one {Label} other {Labels}}.',
    id: 'wk9s/W',
  },
  asyncPrintLabelFailedContent: {
    description: 'Notification message shown when generating a label fails',
    defaultMessage: 'Failed to generate {docCount, plural,=0 {Label} one {Label} other {Labels}}. Please try again.',
    id: 'g0ompl',
  },
  unknownError: {
    description: 'printEpics.unknownError',
    defaultMessage: 'Unknown Error',
    id: 'DEGYz9',
  },
  errorSettingPrintedStatus: {
    description: 'Notification message shown when setting status printed fails',
    defaultMessage: 'An unknown error occurred. Please try again later and contact support if the error occurs again',
    id: 'sP2Swj',
  },
});

export const label = {
  id: DocumentType.Label.identifier,

  getDownloadStartedProps: (docCount) => ({
    title: <FormattedMessage {...messages.downloadLabelsStartedTitle} values={{ docCount }} />,
    content: <FormattedMessage {...messages.downloadLabelsStartedContent} values={{ docCount }} />,
  }),

  getDownloadCompletedProps: (docCount) => ({
    title: <FormattedMessage {...messages.downloadLabelsCompletedTitle} values={{ docCount }} />,
    content: <FormattedMessage {...messages.downloadLabelsCompletedContent} values={{ docCount }} />,
  }),

  getPrintedSuccessNotificationProps: (docCount, printerName) => ({
    title: <FormattedMessage {...messages.printingLabelSuccessTitle} values={{ docCount }} />,
    content: <FormattedMessage {...messages.printingLabelSuccessContent} values={{ docCount, printerName }} />,
  }),

  getPrintedSuccessObservable: (printJob) => {
    const printJobIds = get(printJob, 'ids');
    const printJobRequestId = get(printJob, 'requestId');
    const { fulfillmentPlanIds } = printJob;
    return of(Actions.markLabelPrinted(fulfillmentPlanIds, printJobIds, true), ConnectActions.printDocumentComplete(printJobRequestId));
  },

  getAsyncPrintStartedNotificationProps: (docCount) => ({
    title: <FormattedMessage {...messages.printingLabelSuccessTitle} values={{ docCount }} />,
    content: <FormattedMessage {...messages.asyncPrintLabelStartedContent} values={{ docCount }} />,
    notificationType: NOTIFICATION_TYPE_INFO,
  }),

  getAsyncPrintFailedNotificationProps: (docCount) => ({
    title: <FormattedMessage {...messages.printingLabelSuccessTitle} values={{ docCount }} />,
    content: <FormattedMessage {...messages.asyncPrintLabelFailedContent} values={{ docCount }} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  }),

  getAsyncPrintedStatusUpdateError: () => ({
    title: <FormattedMessage {...messages.unknownError} />,
    content: <FormattedMessage {...messages.errorSettingPrintedStatus} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  }),
};
